import { useEffect, useRef, useState } from "react";
import type { CatalogImageCase, CorrectionWorkflow, MarketplaceCode } from "./types";

export type SavedCorrectionCase = { workflow: CorrectionWorkflow; cases: CatalogImageCase[] };
type View = "work" | "cases";
type WorkspaceState = { scope: string; view: View | null; savedCases: CatalogImageCase[]; source?: object; savedIds: string[] };
const initialState = (scope: string): WorkspaceState => ({ scope, view: null, savedCases: [], savedIds: [] });

/** Keep the successful save visible while a detail refresh is unavailable. Fresh detail wins. */
export function useImageCaseWorkspace({ scope, sellerSku, marketplaces, asins, source, cases, workflows, enabled }: {
  scope: string;
  sellerSku: string;
  marketplaces: MarketplaceCode[];
  asins: Partial<Record<MarketplaceCode, string | null>>;
  source: object;
  cases: CatalogImageCase[];
  workflows: CorrectionWorkflow[] | null;
  enabled: boolean;
}) {
  const [stored, setStored] = useState<WorkspaceState>(() => initialState(scope));
  const current = useRef({ scope, sellerSku, marketplaces, asins, source, enabled });
  current.current = { scope, sellerSku, marketplaces, asins, source, enabled };
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    setStored(previous => previous.scope !== scope ? initialState(scope) : previous.savedCases.length && previous.source !== source ? { ...previous, savedCases: [], source: undefined } : previous);
  }, [scope, source]);
  const state = stored.scope === scope ? stored : initialState(scope);
  const merged = new Map(cases.map(imageCase => [imageCase.id, imageCase]));
  if (state.source === source) for (const imageCase of state.savedCases) {
    const fresh = merged.get(imageCase.id);
    if (!fresh || fresh.revision < imageCase.revision) merged.set(imageCase.id, imageCase);
  }
  const displayedCases = [...merged.values()];
  const visible = displayedCases.filter(imageCase => imageCase.status !== "withdrawn");
  const view: View = state.view ?? (visible.length && workflows?.length === 0 ? "cases" : "work");
  const show = (next: View) => {
    if (!mounted.current || current.current.scope !== scope) return;
    setStored(previous => ({ ...(previous.scope === scope ? previous : initialState(scope)), view: next }));
  };
  const acceptSaved = (result: SavedCorrectionCase): boolean => {
    const active = current.current;
    if (!mounted.current || active.scope !== scope || !active.enabled || result.workflow.sellerSku !== active.sellerSku || !active.marketplaces.includes(result.workflow.marketplaceCode) || active.asins[result.workflow.marketplaceCode] !== result.workflow.asin) return false;
    const savedCases = result.cases.filter(imageCase => imageCase.sellerSku === active.sellerSku && imageCase.marketplaceCode === result.workflow.marketplaceCode && imageCase.asin === result.workflow.asin);
    if (!savedCases.length || savedCases.length !== result.cases.length) return false;
    setStored(previous => {
      const pending = new Map((previous.scope === scope && previous.source === active.source ? previous.savedCases : []).map(imageCase => [imageCase.id, imageCase]));
      for (const imageCase of savedCases) {
        const earlier = pending.get(imageCase.id);
        if (!earlier || earlier.revision <= imageCase.revision) pending.set(imageCase.id, imageCase);
      }
      return { scope, view: "cases", savedCases: [...pending.values()], source: active.source, savedIds: savedCases.map(imageCase => imageCase.id) };
    });
    return true;
  };
  const saved = displayedCases.find(imageCase => state.savedIds.includes(imageCase.id) && imageCase.status === "waiting");
  const caseLabels = [...new Set(visible.map(imageCase => `${imageCase.marketplaceCode}: ${imageCase.status === "resolved" ? "Bilder erneut prüfen" : imageCase.status === "waiting" ? "Fall gespeichert" : "Prüfung fällig"}`))];
  const pendingCountries = [...new Set((workflows ?? []).map(workflow => workflow.marketplaceCode))];
  return { view, cases: displayedCases, saved, caseLabels, pendingCountries, acceptSaved, showWork: () => show("work"), showCases: () => show("cases") };
}
